import Phaser from "phaser";

import {
  OnboardingPointerScene,
  type OnboardingPointerPresentation,
} from "./OnboardingPointerScene";

export type OnboardingPointerRuntime = {
  game: Phaser.Game;
  scene: OnboardingPointerScene;
  ready: Promise<void>;
  renderScale: number;
  setPresentation(presentation: OnboardingPointerPresentation): void;
  resize(width: number, height: number): void;
  destroy(): void;
};

const MAX_RENDER_SCALE = 2;

function getRenderScale(): number {
  return Math.min(MAX_RENDER_SCALE, Math.max(1, window.devicePixelRatio || 1));
}

export function createOnboardingPointerGame(
  parent: HTMLElement,
  presentation: OnboardingPointerPresentation,
): OnboardingPointerRuntime {
  const renderScale = getRenderScale();
  const logicalWidth = parent.clientWidth || window.innerWidth;
  const logicalHeight = parent.clientHeight || window.innerHeight;
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const scene = new OnboardingPointerScene({ onReady: resolveReady }, presentation, renderScale);
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(logicalWidth * renderScale),
    height: Math.round(logicalHeight * renderScale),
    transparent: true,
    backgroundColor: "rgba(0,0,0,0)",
    antialias: true,
    render: { antialias: true, pixelArt: false, roundPixels: false },
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
    scene: [scene],
    banner: false,
  });
  const canvas = parent.querySelector("canvas");
  if (canvas !== null) {
    canvas.style.pointerEvents = "none";
    canvas.setAttribute("aria-hidden", "true");
  }
  return {
    game,
    scene,
    ready,
    renderScale,
    setPresentation(next): void {
      scene.setPresentation(next);
    },
    resize(width, height): void {
      game.scale.resize(Math.round(width * renderScale), Math.round(height * renderScale));
    },
    destroy(): void {
      game.destroy(true);
    },
  };
}
